/**
 * Iter XX.44.L — Partner-type personalization config.
 * =====================================================
 *
 * A single source of truth that maps `partner_type` → the UX affordances
 * that make the dashboard feel purpose-built for that persona.
 *
 * Every consumer (dashboard sections, first-steps banner, discover
 * page, welcome copy, empty states) reads from this module. That way
 * a new partner type is a NEW ENTRY here, not a scavenger hunt through
 * ten JSX files.
 *
 * Contract for each type:
 *   label:          Short human label.
 *   pronoun:        Used in welcome copy ("Academy Partner", "Coach").
 *   summary:        One-line description shown on discover / claim.
 *   benefits:       Ordered bullet list on the discover page.
 *   priority:       Ordered list of dashboard section IDs (see SECTIONS
 *                   const in PartnerDashboard). Anything not listed
 *                   appears after in the default order.
 *   checklist:      Ordered first-steps items shown to a brand-new
 *                   partner. Each item: {code, label, action_path?,
 *                   action_cta?}.
 *   coach_hints:    Small copy override for the Growth Coach card
 *                   heading + neutral fallback line when nothing
 *                   actionable — same principle as the backend
 *                   (grounded, never fabricated).
 *   emptyStates:    Copy for zero-data states, keyed by section.
 *
 * Rule: NEVER fabricate numbers here. Copy is descriptive & grounded.
 */

const BASE = {
  academy: {
    label: "Academy",
    pronoun: "Academy Partner",
    summary: "Coach-first tool for cricket academies. Roster health, team broadcast, weekly digest.",
    benefits: [
      "Anonymised Team Pulse — see roster health without breaching player privacy.",
      "Team Broadcast to reach every registered player at once.",
      "Weekly Digest email that summarises engagement.",
      "Public academy page with achievements, gallery, booking link and Google Maps.",
    ],
    priority: ["first_steps", "wallet", "team_pulse_hint", "metrics", "coach", "sharing", "studio", "activity"],
    checklist: [
      { code: "invite_players", label: "Invite your players to Gully",           action_cta: "Copy referral link",  action_hint: "copy_referral" },
      { code: "first_broadcast", label: "Send your first team broadcast",         action_cta: "Coming soon",         action_hint: "team_broadcast" },
      { code: "team_pulse",     label: "View your Team Pulse once you have 5+ players",  action_cta: "Learn more",   action_hint: "team_pulse" },
      { code: "polish_page",    label: "Add achievements, gallery and booking link",     action_cta: "Edit page →",  action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "Academy Growth Coach",
      neutral: "Your Academy Pulse tools are quietly running in the background. Keep inviting players — signal shows up around 5 members.",
    },
    empty: {
      activity: "No player traffic yet. As players open your page or click through, activity will appear here.",
      metrics:  "Metrics start counting the moment your first player visits your page.",
    },
  },

  coach: {
    label: "Coach",
    pronoun: "Coach",
    summary: "A permanent Gully page for individual coaches. Referral link, marketing drafts, wallet.",
    benefits: [
      "A permanent public page at askgully.com/@yourhandle.",
      "Referral link + one-click sharing pack (bio copy, WhatsApp message, QR).",
      "Marketing Studio drafts (captions, reels, WhatsApp — never posts on your behalf).",
      "Wallet with a per-country cash-out flow.",
    ],
    priority: ["first_steps", "wallet", "metrics", "sharing", "coach", "studio", "activity"],
    checklist: [
      { code: "publish_page",   label: "Publish your Gully page",         action_cta: "Done ✓",        done: true },
      { code: "add_to_bio",     label: "Add your link to Instagram or WhatsApp bio",  action_cta: "Copy public link", action_hint: "copy_public" },
      { code: "invite_first",   label: "Share your page with one player",  action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "grow_page",      label: "Add achievements, testimonials, session pricing",   action_cta: "Edit page →",  action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "Growth Coach",
      neutral: "Your page is live. Keep one share going per week — that's usually enough to keep visits ticking.",
    },
    empty: {
      activity: "No traffic yet. Share your page once — activity will start showing here.",
      metrics:  "Every player visit gets counted. Numbers appear once someone opens your link.",
    },
  },

  creator: {
    label: "Creator",
    pronoun: "Creator Partner",
    summary: "For cricket creators. Marketing Studio, referral analytics, bio-optimised sharing pack.",
    benefits: [
      "Marketing Studio for captions, IG carousels, reel scripts, WhatsApp copy.",
      "Referral analytics grounded in your own audience — never fabricated.",
      "Bio snippet pack sized for Instagram, LinkedIn, and X.",
      "Wallet + weekly Growth Coach nudges.",
    ],
    priority: ["first_steps", "studio", "sharing", "metrics", "coach", "wallet", "activity"],
    checklist: [
      { code: "add_bio",        label: "Add your Gully link to your Instagram / YouTube bio", action_cta: "Copy public link",  action_hint: "copy_public" },
      { code: "first_post",     label: "Draft your first post in Marketing Studio",           action_cta: "Open Studio →",     action_path: "/partner/studio" },
      { code: "invite_audience", label: "Share your referral link with your audience",         action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "polish_page",    label: "Polish your public page — achievements, gallery",     action_cta: "Edit page →",       action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "Creator Coach",
      neutral: "Traffic tends to spike with each post. Marketing Studio has drafts ready when you want to try a new format.",
    },
    empty: {
      activity: "No traffic yet. Push one link out to your audience and this feed starts moving.",
      metrics:  "Visits, clicks and referred players will appear here as your audience arrives.",
    },
  },

  influencer: {
    label: "Influencer",
    pronoun: "Partner",
    summary: "For influencers running paid or unpaid Gully campaigns. Campaign-friendly analytics + wallet.",
    benefits: [
      "Campaign-friendly referral link + one-tap share pack.",
      "Audience-safe analytics — no player-private data, ever.",
      "Marketing Studio drafts for stories, reels and posts.",
      "Wallet with clean per-country cash-out.",
    ],
    priority: ["first_steps", "sharing", "wallet", "studio", "metrics", "coach", "activity"],
    checklist: [
      { code: "add_bio",        label: "Add your Gully link to every platform bio",    action_cta: "Copy public link",  action_hint: "copy_public" },
      { code: "first_campaign", label: "Draft a launch story in Marketing Studio",     action_cta: "Open Studio →",     action_path: "/partner/studio" },
      { code: "share_link",     label: "Push the referral link in your next post",     action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "watch_wallet",   label: "Track your wallet as attributions come in",    action_cta: "Open Wallet →",     action_path: "/partner/wallet" },
    ],
    coach_hints: {
      heading: "Growth Coach",
      neutral: "Attribution takes a few days to settle after a campaign. Keep the referral link consistent so credit lands where it should.",
    },
    empty: {
      activity: "Campaign metrics arrive as soon as your link starts driving visits.",
      metrics:  "Numbers appear as clicks come in from each post.",
    },
  },

  club: {
    label: "Club",
    pronoun: "Club Partner",
    summary: "For cricket clubs. Member roster, team broadcast, engagement pulse.",
    benefits: [
      "Broadcast to every registered member at once — one-way, opt-in.",
      "Anonymised member Pulse — see engagement without opening private conversations.",
      "Public club page with map, gallery, and session pricing.",
      "Weekly Digest so committee members stay informed.",
    ],
    priority: ["first_steps", "team_pulse_hint", "metrics", "sharing", "coach", "wallet", "activity"],
    checklist: [
      { code: "invite_members", label: "Invite club members to Gully",             action_cta: "Copy referral link",  action_hint: "copy_referral" },
      { code: "first_broadcast", label: "Send a welcome broadcast once you have 5+ members", action_cta: "Coming soon", action_hint: "team_broadcast" },
      { code: "view_pulse",     label: "Watch Member Pulse for early engagement signal",     action_cta: "Learn more",   action_hint: "team_pulse" },
      { code: "polish_page",    label: "Add photos, achievements and location on the map",   action_cta: "Edit page →",  action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "Club Growth Coach",
      neutral: "Clubs grow best through steady member invites. Aim for one new player added per week and the pulse will build.",
    },
    empty: {
      activity: "Member activity appears once your first players open the club page.",
      metrics:  "Signal takes ~5 active members to become meaningful.",
    },
  },

  school: {
    label: "School",
    pronoun: "School Partner",
    summary: "For schools & universities. Student & parent communication, team pulse.",
    benefits: [
      "Team communication for coaches → registered students.",
      "Parent engagement without exposing private student conversations.",
      "Weekly Digest that fits inside an existing school newsletter.",
      "Public page with location, session times, and coach bios.",
    ],
    priority: ["first_steps", "team_pulse_hint", "sharing", "metrics", "coach", "wallet", "activity"],
    checklist: [
      { code: "invite_students", label: "Invite your students to Gully",  action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "share_with_parents", label: "Share the page with parents once students join", action_cta: "Copy public link", action_hint: "copy_public" },
      { code: "coach_pulse",    label: "Use Team Pulse to spot which students need extra support", action_cta: "Learn more", action_hint: "team_pulse" },
      { code: "polish_page",    label: "Add coach bios, training schedule, location",              action_cta: "Edit page →", action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "School Growth Coach",
      neutral: "Schools see the best signal once a full squad is on Gully. Focus one invite conversation at a time.",
    },
    empty: {
      activity: "Student activity appears here once they start using Gully.",
      metrics:  "Metrics unlock as your squad grows.",
    },
  },

  league: {
    label: "League",
    pronoun: "League Partner",
    summary: "For tournaments & leagues. Community broadcast, player engagement, event momentum.",
    benefits: [
      "Broadcast tournament announcements to every registered participant.",
      "Aggregate participation pulse — never player-private.",
      "Public league page with schedule, venue map, and organiser contacts.",
      "Weekly Digest of participation and momentum.",
    ],
    priority: ["first_steps", "sharing", "team_pulse_hint", "metrics", "studio", "coach", "wallet", "activity"],
    checklist: [
      { code: "invite_players", label: "Invite tournament participants to Gully",         action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "broadcast_first", label: "Send a fixture-update broadcast once players join", action_cta: "Coming soon",       action_hint: "team_broadcast" },
      { code: "share_schedule", label: "Publish the tournament schedule on your page",     action_cta: "Edit page →",       action_path: "/partner/edit" },
      { code: "post_updates",   label: "Draft a match-day update in Marketing Studio",     action_cta: "Open Studio →",     action_path: "/partner/studio" },
    ],
    coach_hints: {
      heading: "League Growth Coach",
      neutral: "Momentum concentrates around fixtures. Keep updates short and frequent — one broadcast per match-day is a good baseline.",
    },
    empty: {
      activity: "Participant traffic starts as soon as your first players open the league page.",
      metrics:  "Registration and engagement counters activate with your first player visit.",
    },
  },

  other: {
    label: "Partner",
    pronoun: "Partner",
    summary: "A permanent Gully partner page — customise it for your community.",
    benefits: [
      "A permanent public page at askgully.com/@yourhandle.",
      "One-click sharing pack.",
      "Marketing Studio drafts.",
      "Wallet with per-country cash-out.",
    ],
    priority: ["first_steps", "metrics", "sharing", "coach", "wallet", "studio", "activity"],
    checklist: [
      { code: "add_to_bio",     label: "Add your Gully link to your bio",  action_cta: "Copy public link", action_hint: "copy_public" },
      { code: "invite_first",   label: "Invite one person to try it",     action_cta: "Copy referral link", action_hint: "copy_referral" },
      { code: "polish_page",    label: "Fill in the rest of your page",   action_cta: "Edit page →",       action_path: "/partner/edit" },
    ],
    coach_hints: {
      heading: "Growth Coach",
      neutral: "Your page is live. Keep sharing it — traffic compounds surprisingly quickly.",
    },
    empty: {
      activity: "Activity appears when someone visits your page.",
      metrics:  "Metrics start counting from your first visit.",
    },
  },
};

/**
 * Returns the config block for a partner type, falling back to `other`
 * for any unknown / null value. Never throws.
 */
export function getPartnerTypeConfig(partnerType) {
  if (!partnerType) return BASE.other;
  const key = String(partnerType).toLowerCase();
  return BASE[key] || BASE.other;
}

/**
 * Deterministic dashboard section ordering.
 * Any section not present in `priority` is appended in a stable
 * default order so nothing is ever hidden.
 */
export function orderSections(partnerType, defaultOrder) {
  const cfg = getPartnerTypeConfig(partnerType);
  const seen = new Set();
  const out = [];
  for (const id of cfg.priority) {
    if (defaultOrder.includes(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  for (const id of defaultOrder) {
    if (!seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  return out;
}

export const PARTNER_TYPE_KEYS = Object.keys(BASE);
export default { getPartnerTypeConfig, orderSections, PARTNER_TYPE_KEYS };
